import {
  createSlice,
  Draft,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from "@reduxjs/toolkit";

export interface SliceState<T> {
  data?: T;
  initialData?: T;
  hasChanges: boolean;
}
export const createGenericFormSlice = <
  T,
  Reducers extends SliceCaseReducers<SliceState<T>>,
>({
  name = "",
  initialState,
  reducers,
}: {
  name: string;
  initialState: SliceState<T>;
  reducers: ValidateSliceCaseReducers<SliceState<T>, Reducers>;
}) => {
  return createSlice({
    name,
    initialState,
    reducers: {
      initialize: (state: Draft<SliceState<T>>, action: PayloadAction<T>) => {
        // Immer's Draft generic inference regressed for this assignment with newer RTK/TS versions.
        state.data = action.payload as any;
        state.initialData = state.data;
        state.hasChanges = false;
      },
      reset: (state: Draft<SliceState<T>>) => {
        state.data = state.initialData;
        state.hasChanges = false;
      },
      /**
       * If you want to write to values of the state that depend on the generic
       * (in this case: `state.data`, which is T), you might need to specify the
       * State type manually here, as it defaults to `Draft<GenericState<T>>`,
       * which can sometimes be problematic with yet-unresolved generics.
       * This is a general problem when working with immer's Draft type and generics.
       */
      updateField: (
        state: Draft<SliceState<T>>,
        action: PayloadAction<{
          field: keyof T;
          value:
            | string
            | boolean
            | Date
            | number
            | string[]
            | any[]
            | undefined
            | Record<string, any>;
        }>,
      ) => {
        if (state.data) {
          // Mark the state
          state.hasChanges = true;
          if (action.payload.field) {
            (state.data as any)[action.payload.field] = action.payload.value;
          }
        }
      },
    },
    ...reducers,
  });
};
